import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, ExternalLink, Image } from 'lucide-react';
import { getApiBaseUrl } from '@/services/apiClient';
import { SearchInput, RaceSelector, StatusBadge, LoadingState, PageShell, EmptyState } from '@/components/shared';
import type { SponsorsManagerViewProps } from './sponsorsmanager.props';
import SponsorsFormDialog from './sponsorsmanager.form';

const getSponsorImageSrc = (imageUrl: string, isExternalImage: boolean) => {
  if (!imageUrl) return '';
  if (isExternalImage) return imageUrl;
  return `${getApiBaseUrl()}${imageUrl}`;
};

const SponsorsManagerView: React.FC<SponsorsManagerViewProps> = ({
  races, selectedRaceId, setSelectedRaceId, searchQuery, setSearchQuery,
  loading, filteredSponsors, handleAdd, handleEdit, handleDelete,
  isDialogOpen, setIsDialogOpen, editingSponsor, useExternalImage, setUseExternalImage,
  formData, setFormData, uploading, handleImageUpload, handleSubmit,
  deleteTarget, setDeleteTarget, confirmDelete, embedded,
}) => {
  return (
    <PageShell>
      <Card>
        <CardHeader>
          <CardTitle>Спонзори и Партнери</CardTitle>
          <CardDescription>Управување со спонзори и партнери за настани</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            {!embedded && (
              <RaceSelector
                races={races}
                value={selectedRaceId}
                onChange={setSelectedRaceId}
                label="Избери настан"
                className="w-full sm:w-64"
              />
            )}
            <div className="flex-1">
              <Label className="mb-2 block text-sm font-medium">&nbsp;</Label>
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Пребарај спонзори..."
              />
            </div>
            <div>
              <Label className="mb-2 block text-sm font-medium">&nbsp;</Label>
              <Button onClick={handleAdd} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Додај спонзор
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState message="Вчитување спонзори..." />
      ) : !selectedRaceId && !embedded ? (
        <EmptyState message="Изберете настан за да ги видите спонзорите" inCard />
      ) : filteredSponsors.length === 0 ? (
        <EmptyState
          message={searchQuery ? 'Нема резултати за пребарувањето' : 'Нема спонзори за овој настан'}
          inCard
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSponsors
            .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
            .map((sponsor: any) => (
              <Card key={sponsor.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {sponsor.imageUrl ? (
                        <img
                          src={getSponsorImageSrc(sponsor.imageUrl, sponsor.isExternalImage)}
                          alt={sponsor.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <Image className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{sponsor.name}</h3>
                      <StatusBadge
                        label={sponsor.type === 'Sponsor' ? 'Спонзор' : 'Партнер'}
                        variant={sponsor.type === 'Sponsor' ? 'info' : 'success'}
                        className="mt-1 rounded-full"
                      />
                      {sponsor.websiteUrl && (
                        <a
                          href={sponsor.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1 truncate"
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{sponsor.websiteUrl}</span>
                        </a>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Редослед: {sponsor.sortOrder}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(sponsor)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(sponsor.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      <SponsorsFormDialog
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        editingSponsor={editingSponsor}
        useExternalImage={useExternalImage}
        setUseExternalImage={setUseExternalImage}
        formData={formData}
        setFormData={setFormData}
        uploading={uploading}
        handleImageUpload={handleImageUpload}
        handleSubmit={handleSubmit}
        deleteTarget={deleteTarget}
        setDeleteTarget={setDeleteTarget}
        confirmDelete={confirmDelete}
      />
    </PageShell>
  );
};

export default SponsorsManagerView;
